import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Stack,
  IconButton,
  Tooltip,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Collapse,
  Chip,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  ListItemIcon,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';
import HtmlIcon from '@mui/icons-material/Html';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { exportProposal } from '../utils/exportUtils';
import type { ExportFormat } from '../utils/exportUtils';
import PromptsDialog from './PromptsDialog';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface QuestionSummary {
  totalQuestions: number;
  completedQuestions: number;
  failedQuestions: number;
  skippedQuestions: number;
  insufficientInfoQuestions: number;
  failedQuestionDetails?: Array<{
    questionNumber: number;
    questionText: string;
    status: string;
    reason?: string;
  }>;
}

interface ProposalViewerProps {
  content: string;
  rfpId: string;
  proposalId?: string;
  rfpName?: string;
  questionSummary?: QuestionSummary;
}

export default function ProposalViewer({ content, proposalId, rfpName, questionSummary }: Omit<ProposalViewerProps, 'rfpId'> & { rfpId?: string }) {
  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  const [editedContent, setEditedContent] = useState(content);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [promptsDialogOpen, setPromptsDialogOpen] = useState(false);
  const [prompts, setPrompts] = useState<{
    questionExtraction?: string;
    answerGeneration?: string;
    cohesionReview?: string;
  }>({});
  const [loadingPrompts, setLoadingPrompts] = useState(false);

  useEffect(() => {
    setEditedContent(content);
  }, [content]);

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExport = async (format: ExportFormat) => {
    setIsExporting(true);
    handleExportMenuClose();

    try {
      await exportProposal({
        content: editedContent,
        filename: 'proposal',
        format,
      });
      setSnackbarMessage(`Successfully exported as ${format.toUpperCase()}`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Export failed:', error);
      setSnackbarMessage(`Failed to export as ${format.toUpperCase()}`);
      setSnackbarOpen(true);
    } finally {
      setIsExporting(false);
    }
  };

  const handleGoogleDriveExport = async () => {
    setIsExporting(true);
    handleExportMenuClose();

    try {
      const response = await fetch('/api/google-drive/upload-proposal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          content: editedContent,
          fileName: 'Proposal',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to upload to Google Drive');
      }

      const result = await response.json();
      setSnackbarMessage('Successfully uploaded to Google Drive!');
      setSnackbarOpen(true);

      // Open the document in a new tab
      if (result.webViewLink) {
        window.open(result.webViewLink, '_blank');
      }
    } catch (error) {
      console.error('Google Drive export failed:', error);
      if (error instanceof Error && error.message.includes('not connected')) {
        setSnackbarMessage('Please connect Google Drive first in Context page');
      } else {
        setSnackbarMessage('Failed to upload to Google Drive');
      }
      setSnackbarOpen(true);
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editedContent);
      setSnackbarMessage('Copied to clipboard');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy:', err);
      setSnackbarMessage('Failed to copy to clipboard');
      setSnackbarOpen(true);
    }
  };

  const handleViewPrompts = async () => {
    if (!proposalId) {
      setSnackbarMessage('Proposal ID not available');
      setSnackbarOpen(true);
      return;
    }

    setLoadingPrompts(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/rfp/history/${proposalId}/prompts`, {
        withCredentials: true,
      });
      setPrompts(response.data.prompts || {});
      setPromptsDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
      // Still open dialog to show "not available" message
      setPrompts({});
      setPromptsDialogOpen(true);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const completionRate = questionSummary
    ? Math.round((questionSummary.completedQuestions / questionSummary.totalQuestions) * 100)
    : 100;

  const hasIssues = questionSummary && (
    questionSummary.failedQuestions > 0 ||
    questionSummary.skippedQuestions > 0 ||
    questionSummary.insufficientInfoQuestions > 0
  );

  return (
    <Card elevation={2}>
      <CardContent>
        <Stack spacing={2}>
          {/* Question Summary Alert */}
          {questionSummary && (
            <Alert
              severity={completionRate === 100 ? 'success' : completionRate >= 80 ? 'warning' : 'error'}
              icon={completionRate === 100 ? <CheckCircleIcon /> : <WarningIcon />}
              action={
                hasIssues && (
                  <IconButton
                    size="small"
                    onClick={() => setDetailsExpanded(!detailsExpanded)}
                    sx={{ ml: 1 }}
                  >
                    {detailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                )
              }
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {questionSummary.completedQuestions} of {questionSummary.totalQuestions} questions answered ({completionRate}%)
                </Typography>
                <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                  {questionSummary.completedQuestions > 0 && (
                    <Chip
                      label={`${questionSummary.completedQuestions} completed`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {questionSummary.failedQuestions > 0 && (
                    <Chip
                      label={`${questionSummary.failedQuestions} failed`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  )}
                  {questionSummary.skippedQuestions > 0 && (
                    <Chip
                      label={`${questionSummary.skippedQuestions} skipped`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                  {questionSummary.insufficientInfoQuestions > 0 && (
                    <Chip
                      label={`${questionSummary.insufficientInfoQuestions} insufficient info`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Stack>

                {/* Expandable Details */}
                <Collapse in={detailsExpanded} timeout="auto" unmountOnExit>
                  {questionSummary.failedQuestionDetails && questionSummary.failedQuestionDetails.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="caption" fontWeight={600} display="block" mb={1}>
                        Questions not completed:
                      </Typography>
                      <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                        {questionSummary.failedQuestionDetails.map((detail) => (
                          <ListItem key={detail.questionNumber} sx={{ py: 0.5 }}>
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight={500}>
                                  Q{detail.questionNumber}: {detail.questionText.slice(0, 60)}...
                                </Typography>
                              }
                              secondary={
                                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                                  <Chip
                                    label={detail.status}
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                  {detail.reason && (
                                    <Typography variant="caption" color="text.secondary">
                                      {detail.reason.slice(0, 100)}
                                    </Typography>
                                  )}
                                </Stack>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Collapse>
              </Box>
            </Alert>
          )}

          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Typography variant="h6" component="h3">
              Generated Proposal
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_, newMode) => newMode && setMode(newMode)}
                size="small"
              >
                <ToggleButton value="preview">
                  <PreviewIcon sx={{ mr: 0.5, fontSize: 20 }} />
                  Preview
                </ToggleButton>
                <ToggleButton value="edit">
                  <EditIcon sx={{ mr: 0.5, fontSize: 20 }} />
                  Edit
                </ToggleButton>
              </ToggleButtonGroup>

              <Tooltip title="Copy to clipboard">
                <IconButton onClick={handleCopy} color="primary" size="small">
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>

              {proposalId && (
                <Tooltip title="View prompts used">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CodeIcon />}
                    onClick={handleViewPrompts}
                    disabled={loadingPrompts}
                  >
                    Prompts
                  </Button>
                </Tooltip>
              )}

              <Button
                variant="contained"
                startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                onClick={handleExportMenuOpen}
                size="small"
                disabled={isExporting}
                data-download-button
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #5a3a7a 100%)',
                  },
                }}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleExportMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={() => handleExport('pdf')}>
                  <ListItemIcon>
                    <PictureAsPdfIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="body2">Export as PDF</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleExport('docx')}>
                  <ListItemIcon>
                    <DescriptionIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="body2">Export as DOCX</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleExport('md')}>
                  <ListItemIcon>
                    <CodeIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="body2">Export as Markdown</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleExport('html')}>
                  <ListItemIcon>
                    <HtmlIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="body2">Export as HTML</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleExport('txt')}>
                  <ListItemIcon>
                    <TextFieldsIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="body2">Export as Text</Typography>
                </MenuItem>
                <MenuItem
                  onClick={handleGoogleDriveExport}
                  sx={{ borderTop: 1, borderColor: 'divider', mt: 0.5, pt: 1 }}
                >
                  <ListItemIcon>
                    <CloudUploadIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <Typography variant="body2" color="primary">
                    Export to Google Drive
                  </Typography>
                </MenuItem>
              </Menu>
            </Stack>
          </Box>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            message={snackbarMessage}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          />

          {mode === 'edit' ? (
            <TextField
              multiline
              fullWidth
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              variant="outlined"
              minRows={20}
              maxRows={30}
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'Georgia, serif',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                },
              }}
            />
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                backgroundColor: 'background.default',
                maxHeight: '70vh',
                overflowY: 'auto',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => (
                    <Typography
                      variant="h3"
                      component="h1"
                      sx={{
                        fontWeight: 700,
                        mb: 3,
                        mt: 2,
                        color: 'primary.main',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                      }}
                    >
                      {children}
                    </Typography>
                  ),
                  h2: ({ children }) => (
                    <Typography
                      variant="h4"
                      component="h2"
                      sx={{
                        fontWeight: 600,
                        mb: 2.5,
                        mt: 4,
                        color: 'text.primary',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                      }}
                    >
                      {children}
                    </Typography>
                  ),
                  h3: ({ children }) => (
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        mt: 3,
                        color: 'text.primary',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                      }}
                    >
                      {children}
                    </Typography>
                  ),
                  p: ({ children }) => (
                    <Typography
                      component="p"
                      sx={{
                        fontFamily: 'Georgia, serif',
                        fontSize: '1rem',
                        lineHeight: 1.8,
                        mb: 2,
                        color: 'text.primary',
                      }}
                    >
                      {children}
                    </Typography>
                  ),
                  ul: ({ children }) => (
                    <Box
                      component="ul"
                      sx={{
                        fontFamily: 'Georgia, serif',
                        fontSize: '1rem',
                        lineHeight: 1.8,
                        mb: 2,
                        pl: 4,
                        color: 'text.primary',
                      }}
                    >
                      {children}
                    </Box>
                  ),
                  ol: ({ children }) => (
                    <Box
                      component="ol"
                      sx={{
                        fontFamily: 'Georgia, serif',
                        fontSize: '1rem',
                        lineHeight: 1.8,
                        mb: 2,
                        pl: 4,
                        color: 'text.primary',
                      }}
                    >
                      {children}
                    </Box>
                  ),
                  li: ({ children }) => (
                    <Box
                      component="li"
                      sx={{
                        mb: 1,
                      }}
                    >
                      {children}
                    </Box>
                  ),
                  strong: ({ children }) => (
                    <Box
                      component="strong"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                      }}
                    >
                      {children}
                    </Box>
                  ),
                  em: ({ children }) => (
                    <Box
                      component="em"
                      sx={{
                        fontStyle: 'italic',
                      }}
                    >
                      {children}
                    </Box>
                  ),
                  code: ({ children }) => (
                    <Box
                      component="code"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        backgroundColor: 'action.hover',
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 1,
                      }}
                    >
                      {children}
                    </Box>
                  ),
                  blockquote: ({ children }) => (
                    <Box
                      component="blockquote"
                      sx={{
                        borderLeft: 4,
                        borderColor: 'primary.main',
                        pl: 2,
                        ml: 0,
                        my: 2,
                        fontStyle: 'italic',
                        color: 'text.secondary',
                      }}
                    >
                      {children}
                    </Box>
                  ),
                }}
              >
                {editedContent}
              </ReactMarkdown>
            </Paper>
          )}
        </Stack>
      </CardContent>

      <PromptsDialog
        open={promptsDialogOpen}
        rfpName={rfpName || 'Proposal'}
        prompts={prompts}
        onClose={() => setPromptsDialogOpen(false)}
      />
    </Card>
  );
}
